import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Paperclip,
  Download,
  Link2,
  ExternalLink,
  TicketPlus,
  Check,
  FileX,
  Pencil,
  Trash2,
  Ban,
  Languages,
  Loader2,
} from 'lucide-react';
import { clockTime, dayHeading } from '../lib/format.js';
import {
  translationAvailable,
  translationSupported,
  translateToEnglish,
  cachedTranslation,
  cacheKeyFor,
  languageName,
} from '../lib/translate.js';
import { api } from '../lib/api.js';
import {
  attachmentUrl,
  formatBytes,
  isImage,
  attachmentRetentionDays,
} from '../lib/upload.js';

/**
 * One attachment, plus what can be done with it.
 *
 * On a private bucket there is no durable URL to put in an `href`, so every
 * action resolves a fresh signed URL at the moment it is used. That is also
 * what makes "Copy link" meaningful: what lands on the clipboard is a link
 * that works for whoever it is pasted to, and stops working shortly after.
 */
function Attachment({ appId, file, onAttachToTicket }) {
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const image = isImage(file.mimeType);

  // Images resolve eagerly — a thumbnail nobody asked for is the point.
  useEffect(() => {
    if (file.expired || !image || !appId || !file.key) return undefined;
    let live = true;
    attachmentUrl(appId, file)
      .then((url) => live && setPreview(url))
      .catch(() => live && setError('Preview unavailable'));
    return () => {
      live = false;
    };
  }, [appId, file, image]);

  const open = async (options) => {
    setBusy(true);
    setError(null);
    try {
      const url = await attachmentUrl(appId, file, options);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    setBusy(true);
    setError(null);
    try {
      const url = await attachmentUrl(appId, file);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The object is gone, but the message still says a file was sent — so the
   * row stays and explains itself. Offering actions that can only fail would
   * be worse than saying plainly that there is nothing to open.
   */
  if (file.expired) {
    const days = attachmentRetentionDays();
    return (
      <div className="file file--expired">
        <div className="file__row">
          <FileX size={11} />
          <span className="file__name">{file.name || 'Attachment'}</span>
          <span className="file__size">
            {days ? `removed after ${days} days` : 'no longer stored'}
            {file.expiredAt ? ` · ${new Date(file.expiredAt).toLocaleDateString()}` : ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`file${image ? ' file--image' : ''}`}>
      {image && preview && (
        <button type="button" className="file__thumb" onClick={() => open()} title="Open full size">
          <img src={preview} alt={file.name || 'Attachment'} loading="lazy" />
        </button>
      )}

      <div className="file__row">
        <Paperclip size={11} />
        <button type="button" className="file__name" onClick={() => open()} disabled={busy}>
          {file.name || 'Attachment'}
        </button>
        {file.size > 0 && <span className="file__size">{formatBytes(file.size)}</span>}

        <div className="file__actions">
          <button type="button" className="icon-button" onClick={copy} title="Copy a shareable link">
            {copied ? <Check size={12} /> : <Link2 size={12} />}
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={() => open({ download: true })}
            title="Download"
          >
            <Download size={12} />
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={() => open()}
            title="Open in a new tab"
          >
            <ExternalLink size={12} />
          </button>
          {onAttachToTicket && (
            <button
              type="button"
              className="icon-button"
              onClick={() => onAttachToTicket(file)}
              title="Attach to a ticket"
            >
              <TicketPlus size={12} />
            </button>
          )}
        </div>
      </div>

      {error && <div className="file__error">{error}</div>}
    </div>
  );
}

function Attachments({ items, appId, onAttachToTicket }) {
  if (!items?.length) return null;
  return (
    <div className="bubble__files">
      {items.map((file) => (
        <Attachment
          key={file.key || file.url}
          appId={appId}
          file={file}
          onAttachToTicket={onAttachToTicket}
        />
      ))}
    </div>
  );
}

/**
 * Rewriting a message in place, where the bubble is.
 *
 * Deliberately not the composer: the composer is where you write the *next*
 * thing, and borrowing it to change an old message loses your draft and hides
 * which bubble is being edited. Enter saves, Escape abandons — the same two
 * keys the thread rename already uses.
 */
function EditBox({ message, onCancel, onSave }) {
  const [draft, setDraft] = useState(message.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === message.body) return onCancel();
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
      onCancel();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="msg-edit">
      <textarea
        className="msg-edit__input"
        autoFocus
        rows={Math.min(8, draft.split('\n').length + 1)}
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            commit();
          }
        }}
      />
      {error && <div className="msg-edit__error">{error}</div>}
      <div className="msg-edit__actions">
        <button type="button" className="ghost-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="button button--primary" onClick={commit} disabled={busy}>
          Save
        </button>
      </div>
    </div>
  );
}

/**
 * Whether this browser can translate on its own, asked once at module load.
 *
 * A button that is permanently dead on every bubble in the thread is worse
 * than no button: it is forty pieces of furniture advertising something the
 * agent cannot have. Where nothing can answer the click — no browser API and
 * no server fallback — the control simply is not rendered.
 */
const CAN_TRANSLATE = translationAvailable();

/**
 * Whether this server will translate for browsers that cannot — Safari, in
 * practice, which ships neither of the APIs above.
 *
 * One request per tab, shared by every bubble: forty of these firing as a
 * thread paints would be forty answers to the same question. The flag arrives
 * a moment after the first render, which is why `translationSupported` is asked
 * per render rather than frozen at module load like `CAN_TRANSLATE`.
 */
let configPromise = null;
function useServerTranslation() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // On-device already covers this browser; asking the server whether it
    // *could* have helped answers a question nobody is going to ask.
    if (CAN_TRANSLATE) return undefined;

    configPromise ??= api.config().catch(() => null);
    let alive = true;
    configPromise.then((config) => {
      if (alive) setEnabled(Boolean(config?.translationEnabled));
    });
    return () => {
      alive = false;
    };
  }, []);

  return enabled;
}

function Bubble({
  message,
  appId,
  onAttachToTicket,
  canModify,
  onEdit,
  onDelete,
  /** True when this browser has no translator and the server offers one. */
  serverTranslation,
}) {
  const outbound = message.senderType === 'agent';
  const [editing, setEditing] = useState(false);

  /**
   * Translation state, per bubble and per session.
   *
   * `showing` is the toggle: the original is never replaced, only covered, so
   * clicking again is a return rather than a second translation. The text
   * itself comes from a module-level cache seeded here, which is what makes
   * leaving the thread and coming back instant — and what makes a reload
   * forget everything, as asked.
   */
  const key = cacheKeyFor(message);
  const [translated, setTranslated] = useState(() => cachedTranslation(message));
  const [showing, setShowing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [from, setFrom] = useState(null);
  /* Said in the meta line, because the two paths are not the same promise: an
     on-device translation never left the machine, a server one did. */
  const [viaServer, setViaServer] = useState(false);
  /** Only ever a problem worth reading — success says so in the meta line. */
  const [note, setNote] = useState(null);

  // An edit changes the key, and with it everything above: the new words are
  // untranslated until somebody asks again.
  useEffect(() => {
    setTranslated(cachedTranslation(message));
    setShowing(false);
    setViaServer(false);
    setNote(null);
    // The key *is* the dependency — it folds in both the id and the edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const toggleTranslation = async () => {
    setNote(null);
    if (showing) return setShowing(false);
    if (translated) return setShowing(true);

    setTranslating(true);
    try {
      const result = await translateToEnglish(message, {
        // Only ever reached by a browser with no translator of its own —
        // `translateToEnglish` enforces that order, not this call site.
        server: serverTranslation
          ? () => api.conversations.translateMessage(appId, message.conversationId, message.id)
          : undefined,
      });
      if (result.alreadyEnglish) {
        setNote('Already in English.');
        return;
      }
      setTranslated(result.text);
      setFrom(result.sourceLanguage ?? null);
      setViaServer(Boolean(result.viaServer));
      setShowing(true);
    } catch (err) {
      setNote(err.message);
    } finally {
      setTranslating(false);
    }
  };

  if (message.senderType === 'system') {
    return <div className="msg msg--system">{message.body}</div>;
  }

  /**
   * A withdrawn message.
   *
   * The row stays and says so rather than vanishing, for the same reason an
   * expired attachment does: the visitor may well have read it, and a thread
   * that silently loses a bubble reads as a bug to the one person who
   * remembers it being there.
   */
  if (message.deleted) {
    return (
      <div className={`msg-wrap msg-wrap--${outbound ? 'out' : 'in'}`}>
        <div className="msg msg--deleted">
          <Ban size={11} />
          Message deleted
        </div>
        <div className="msg__meta">
          {outbound && message.senderName ? `${message.senderName} · ` : ''}
          {clockTime(message.createdAt)}
        </div>
      </div>
    );
  }

  // An emergency the agent sent: red rule plus the audit line, because this
  // one took over the merchant's screen.
  if (message.isEmergency && outbound) {
    return (
      <div className="msg msg--emergency">
        <div className="msg__emergency-tag">
          <AlertCircle size={11} />
          USER ALERT · CHAT FORCE-OPENED
        </div>
        <div className="msg__emergency-body">{message.body}</div>
        <div className="msg__emergency-foot">
          {clockTime(message.createdAt)}
          {message.readAt ? ' · acknowledged by merchant' : ' · awaiting acknowledgement'}
        </div>
      </div>
    );
  }

  const meta = message.failed
    ? 'Not sent'
    : message.pending
      ? 'Sending…'
      : `${clockTime(message.createdAt)}${message.editedAt ? ' · edited' : ''}${
          outbound ? (message.readAt ? ' · Read' : ' · Delivered') : ''
        }`;

  /* Nothing that has not landed yet can be rewritten: an optimistic bubble has
     no id on the server to address, and a failed one was never sent at all. */
  const editable = canModify && !message.pending && !message.failed;

  /* Offered on either side of the thread. A visitor writing in Portuguese is
     the obvious case, but an agency answering in one language and reading a
     colleague's reply in another is the same problem. */
  const translatable =
    translationSupported(serverTranslation) && Boolean(message.body) && !message.pending;

  if (editing) {
    return (
      <div className={`msg-wrap msg-wrap--${outbound ? 'out' : 'in'}`}>
        <EditBox message={message} onCancel={() => setEditing(false)} onSave={onEdit} />
      </div>
    );
  }

  return (
    <div className={`msg-wrap msg-wrap--${outbound ? 'out' : 'in'}`}>
      <div
        className={`msg msg--${outbound ? 'out' : 'in'}${message.failed ? ' is-failed' : ''}${
          showing ? ' is-translated' : ''
        }`}
      >
        {/* The original is covered, never replaced — which is what makes the
            second click a return rather than a second translation. */}
        {showing && translated ? translated : message.body}
        <Attachments
          items={message.attachments}
          appId={appId}
          onAttachToTicket={onAttachToTicket}
        />

        {/* On hover, over the bubble's own corner. Kept out of the meta line so
            a thread at rest still reads as a conversation rather than a table
            of controls. An attachment-only message can be withdrawn but not
            rewritten — there is no text to change. */}
        {(editable || translatable) && (
          <div className="msg__tools">
            {translatable && (
              <button
                type="button"
                className={`icon-button${showing ? ' is-active' : ''}`}
                onClick={toggleTranslation}
                disabled={translating}
                aria-pressed={showing}
                title={
                  showing
                    ? 'Show the original'
                    : translated
                      ? 'Show the English translation'
                      : CAN_TRANSLATE
                        ? 'Translate to English on this device'
                        : // Worth saying before the click, not after: this
                          // browser cannot do it locally, so the text of this
                          // one message is sent to the server to be translated.
                          'Translate to English — this browser has no translator, so this message is sent to the server'
                }
                aria-label={showing ? 'Show the original message' : 'Translate to English'}
              >
                {translating ? (
                  <Loader2 size={11} className="spin" />
                ) : (
                  <Languages size={11} />
                )}
              </button>
            )}
            {editable && message.body && (
              <button
                type="button"
                className="icon-button"
                onClick={() => setEditing(true)}
                title="Edit this message"
                aria-label="Edit this message"
              >
                <Pencil size={11} />
              </button>
            )}
            <button
              type="button"
              className="icon-button is-danger"
              onClick={() => onDelete(message)}
              title="Delete this message"
              aria-label="Delete this message"
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
      <div className="msg__meta">
        {outbound && message.senderName ? `${message.senderName} · ` : ''}
        {meta}
        {/* Says the bubble is not in the words it was sent in, and offers the
            way back in the same breath. Without this an agent could quote a
            translation to the customer believing it was what they wrote. */}
        {showing && (
          <>
            {' · '}
            {from ? `translated from ${languageName(from)}` : 'translated'}
            {viaServer ? ' on the server' : ''}
            {' · '}
            <button type="button" className="msg__untranslate" onClick={() => setShowing(false)}>
              show original
            </button>
          </>
        )}
      </div>

      {note && <div className="msg__note">{note}</div>}
    </div>
  );
}

export function Thread({
  messages,
  visitorTyping,
  agentTyping,
  hasMore,
  onLoadEarlier,
  appId,
  onAttachToTicket,
  /** Answers "may I rewrite this one?" per message; absent means nobody may. */
  canModifyMessage,
  onEditMessage,
  onDeleteMessage,
}) {
  const scroller = useRef(null);
  const pinned = useRef(true);
  /* Asked once for the whole thread, not once per bubble. */
  const serverTranslation = useServerTranslation();

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    // Don't yank the view away from someone reading history.
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, visitorTyping]);

  let lastDay = null;

  return (
    <div className="thread" ref={scroller} onScroll={onScroll} role="log" aria-live="polite">
      {hasMore && (
        <button type="button" className="thread__more" onClick={onLoadEarlier}>
          Load earlier messages
        </button>
      )}

      {messages.length === 0 && (
        <div className="thread__empty">No messages in this conversation yet.</div>
      )}

      {messages.map((message) => {
        const heading = dayHeading(message.createdAt);
        const divider = heading !== lastDay ? heading : null;
        lastDay = heading;

        return (
          <div key={message.id}>
            {divider && <div className="thread__day">{divider}</div>}
            <Bubble
              message={message}
              appId={appId}
              onAttachToTicket={onAttachToTicket}
              canModify={Boolean(canModifyMessage?.(message))}
              onEdit={(body) => onEditMessage(message, body)}
              onDelete={onDeleteMessage}
              serverTranslation={serverTranslation}
            />
          </div>
        );
      })}

      {visitorTyping && (
        <div className="typing" aria-label="Merchant is typing">
          <span />
          <span />
          <span />
        </div>
      )}

      {/* A colleague composing a reply, on the same side of the thread their
          message will land on. Without it the first sign of a second agent is
          two answers arriving at once. */}
      {agentTyping && (
        <div className="typing typing--agent" aria-label={`${agentTyping} is typing`}>
          <span className="typing__who">{agentTyping} is typing</span>
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
}

export default Thread;
